import React from 'react';
import { Box, BoxProps } from '@withjoy/joykit';
import { BreakpointAliasKey, pxToRem } from '@withjoy/joykit/theme';

const ALOHA_BREAKPOINT: BreakpointAliasKey = 'sm4';

type Alignment = 'center' | 'right';

type FixedActionButtonContainerProps = Readonly<{
  children: React.ReactNode;
  onClick?: () => void;
  cursor?: string;
  alignment?: Alignment;
}> &
  BoxProps;

/**
 * Shared container component for fixed action buttons (Join Livestream, RSVP, Guest Guide Trigger).
 * Provides consistent positioning and styling across all fixed action buttons.
 * @param alignment - 'center' centers on both mobile and desktop, 'right' centers on mobile and aligns right on desktop (default)
 */
export const FixedActionButtonContainer = React.forwardRef<HTMLButtonElement, FixedActionButtonContainerProps>(
  ({ children, onClick, cursor = 'pointer', alignment = 'right', ...boxProps }, ref) => {
    const isCenterAligned = alignment === 'center';

    return (
      <Box
        ref={ref}
        as="button"
        position="fixed"
        bottom={{
          _: pxToRem(16),
          [ALOHA_BREAKPOINT]: pxToRem(44),
        }}
        right={{
          _: '50%',
          [ALOHA_BREAKPOINT]: isCenterAligned ? '50%' : pxToRem(44),
        }}
        transform={{
          _: `translate(50%, 0)`,
          [ALOHA_BREAKPOINT]: isCenterAligned ? 'translate(50%, 0)' : 'translate(0, 0)',
        }}
        display="flex"
        alignItems="center"
        gap={4}
        zIndex={100}
        minHeight={pxToRem(64)}
        backgroundColor="rgba(255, 255, 255, 0.64)"
        border="1px solid rgba(255, 255, 255, 0.50)"
        boxShadow="0 4px 32px rgba(0, 0, 0, 0.16)"
        backdropFilter="blur(24px)"
        borderRadius="full"
        paddingY={4}
        paddingRight={5}
        paddingLeft={6}
        cursor={cursor}
        _hover={{
          boxShadow: '0 4px 32px rgba(0, 0, 0, 0.2)',
        }}
        onClick={onClick}
        {...boxProps}
      >
        {children}
      </Box>
    );
  },
);

FixedActionButtonContainer.displayName = 'FixedActionButtonContainer';
